import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { IndicatorsService } from '@modules/indicadores/services/indicators.service';
import { Indicador, Responsavel } from '@core/models/indicator.model';

import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { SelectButton } from 'primeng/selectbutton';
import { PeriodicidadeIndicador } from '@core/models/indicator.model';

@Component({
  selector: 'app-indicator-edit',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, FormsModule, Button, Dialog, ToggleSwitch, SelectButton],
  templateUrl: './indicator-edit.html',
  styleUrl: './indicator-edit.css',
})
export class IndicatorEditComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly indicatorsService = inject(IndicatorsService);

  protected readonly indicatorId = this.route.snapshot.paramMap.get('id');
  protected readonly isEdit = !!this.indicatorId;

  protected readonly form = signal<Partial<Indicador>>({
    sigla: '',
    nome: '',
    unidade_medida: '',
    ativo: true,
    periodicidade: 'MENSAL'
  });

  protected readonly periodicidadeOptions = [
    { label: 'Mensal', value: 'MENSAL' as PeriodicidadeIndicador },
    { label: 'Anual', value: 'ANUAL' as PeriodicidadeIndicador },
  ];
  protected readonly loadingIndicator = signal(this.isEdit);
  protected readonly loadFailed = signal(false);
  private originalAtivo = true;

  protected readonly responsaveis = signal<Responsavel[]>([]);
  protected readonly newResponsibleEmail = signal('');
  protected readonly submitting = signal(false);
  protected readonly showInactivateConfirmation = signal(false);
  protected readonly error = signal('');

  async ngOnInit() {
    if (this.isEdit && this.indicatorId) {
      try {
        const existing = await this.indicatorsService.getById(this.indicatorId);
        this.form.set({ ...existing, periodicidade: existing.periodicidade || 'MENSAL' });
        this.originalAtivo = existing.ativo;
        await this.loadResponsaveis();
      } catch (err: any) {
        this.loadFailed.set(true);
        this.error.set(err?.message || 'Indicador não encontrado.');
      } finally {
        this.loadingIndicator.set(false);
      }
    }
  }

  private async loadResponsaveis() {
    if (this.indicatorId) {
      const data = await this.indicatorsService.getResponsaveis(this.indicatorId);
      this.responsaveis.set(data);
    }
  }

  requestSave() {
    if (this.originalAtivo && this.form().ativo === false) {
      this.showInactivateConfirmation.set(true);
      return;
    }
    void this.save();
  }

  async save() {
    this.submitting.set(true);
    this.error.set('');

    const data = { ...this.form() };
    if (!data.unidade_medida || !data.unidade_medida.trim()) {
      data.unidade_medida = 'unidades';
    }

    try {
      if (this.isEdit && this.indicatorId) {
        await this.indicatorsService.update(this.indicatorId, data);
      } else {
        await this.indicatorsService.create(data);
      }
      this.router.navigate(['/indicadores']);
    } catch (err: any) {
      this.error.set(err.message);
    } finally {
      this.submitting.set(false);
    }
  }

  updateAtivo(ativo: boolean) {
    this.form.update(value => ({ ...value, ativo }));
  }

  updatePeriodicidade(periodicidade: PeriodicidadeIndicador) {
    this.form.update(value => ({ ...value, periodicidade }));
  }

  async addResponsible() {
    if (!this.indicatorId || !this.newResponsibleEmail().trim()) return;
    
    this.error.set('');
    try {
      await this.indicatorsService.assignUser(this.indicatorId, this.newResponsibleEmail());
      this.newResponsibleEmail.set('');
      await this.loadResponsaveis();
    } catch (err: any) {
      this.error.set(err.message);
    }
  }

  async removeResponsible(id: number) {
    try {
      await this.indicatorsService.removeAssignment(id);
      await this.loadResponsaveis();
    } catch (err: any) {
      this.error.set(err.message);
    }
  }

  confirmInactivateAndSave() {
    this.showInactivateConfirmation.set(false);
    void this.save();
  }
}
